import { useTranslate } from 'hooks/useTranslate';
import { FC, useState, useCallback, useRef } from 'react';
import { Modal } from 'react-bootstrap';

import errorSwal from 'utils/errorSwal';

import { useCapitalDecreaseModal } from '../context/CapitalDecreaseModalContext';
import {
  automateDecreaseOperation,
  AutomationLogEntry,
} from '../../SharesTransfer/utils/automateDecreaseOperation';
import {
  axaDecreaseParticipantsList,
  decreaseAutomationConfig,
} from '../../SharesTransfer/constants/axaDecreaseParticipantsList';
import { baseDataForCapitalDecrease } from '../constants/baseDataForCapitalDecrease';
import AutomationLogPanel from './AutomationLogPanel';

const CapitalDecreaseModalFooter: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useCapitalDecreaseModal();
  const [isRunningAutomation, setIsRunningAutomation] = useState(false);
  const [automationLogs, setAutomationLogs] = useState<
    (AutomationLogEntry & { id: number })[]
  >([]);
  const logIdRef = useRef(0);

  const hasValidDate = data.date && data.date >= data.lastOperationDate;

  const buttonEnabledSaveMovement =
    data.sharesRanges.length > 0 && data.remainingShares >= 0;

  const buttonEnabledScriptureView =
    data.scriptureView &&
    hasValidDate &&
    data.totalSharesToDecrease < data.societyActualShares &&
    data.totalSharesToDecrease > 0;

  const buttonEnabledMovementsView =
    !data.scriptureView &&
    data.remainingShares === 0 &&
    data.sharesRanges.length === 0;

  const buttonText = data.scriptureView ? t('Next') : t('SaveOperation');

  const handleRunAutomation = useCallback(async () => {
    setIsRunningAutomation(true);
    setAutomationLogs([]);
    try {
      const onLogCb = (entry: AutomationLogEntry) => {
        logIdRef.current += 1;
        setAutomationLogs((prev) => [
          ...prev,
          { ...entry, id: logIdRef.current },
        ]);
      };

      const onMovementSavedCb = (
        movementParticipant: {
          partnerSearchTerm: string;
          sharesToDecrease?: number;
        },
        index: number
      ) => {
        // eslint-disable-next-line no-console -- intentional log for automation debugging
        console.log(
          `[Automation] Movement saved [${index}]:`,
          movementParticipant
        );
      };

      const config = data.scriptureView
        ? {
            runScripturePhase: true,
            date: data.date,
            totalSharesToDecrease: data.totalSharesToDecrease,
            defaultClassSearchTerm:
              decreaseAutomationConfig.defaultClassSearchTerm,
            onLog: onLogCb,
            onMovementSaved: onMovementSavedCb,
          }
        : {
            defaultClassSearchTerm:
              decreaseAutomationConfig.defaultClassSearchTerm,
            onLog: onLogCb,
            onMovementSaved: onMovementSavedCb,
          };
      await automateDecreaseOperation(axaDecreaseParticipantsList, config);
    } catch (error) {
      const errMsg = (error as Error).message;
      logIdRef.current += 1;
      setAutomationLogs((prev) => [
        ...prev,
        {
          msg: `ERROR: ${errMsg}`,
          type: 'error' as const,
          id: logIdRef.current,
        },
      ]);
      errorSwal(errMsg);
    } finally {
      setIsRunningAutomation(false);
    }
  }, [data.scriptureView, data.date, data.totalSharesToDecrease]);

  const handleClearLogs = useCallback(() => setAutomationLogs([]), []);

  const showAutomationButton =
    process.env.NODE_ENV === 'development' &&
    axaDecreaseParticipantsList.length > 0;

  const showUploadMockButton =
    process.env.NODE_ENV === 'development' &&
    baseDataForCapitalDecrease.partners.length > 0;

  const automationButtonDisabled =
    isRunningAutomation || (data.scriptureView && !buttonEnabledScriptureView);

  return (
    <>
      <AutomationLogPanel
        logs={automationLogs}
        isRunning={isRunningAutomation}
        onClear={handleClearLogs}
      />
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="d-flex">
            {!data.scriptureView && (
              <button
                type="button"
                id="save-movement-button"
                className="btn btn-lg btn-primary"
                onClick={actions.handleSaveMovement}
                disabled={!buttonEnabledSaveMovement}
              >
                {t('SaveMovement')}
              </button>
            )}
            {showAutomationButton && (
              <button
                type="button"
                className="btn btn-lg btn-outline-secondary ml-2"
                onClick={handleRunAutomation}
                disabled={automationButtonDisabled}
              >
                {isRunningAutomation ? '...' : t('Automation')}
              </button>
            )}
            {showUploadMockButton && (
              <button
                type="button"
                className="btn btn-lg btn-outline-secondary ml-2"
                onClick={actions.handleUploadMock}
              >
                {t('UploadMock')}
              </button>
            )}
          </div>
          <button
            type="button"
            className="btn btn-lg btn-primary"
            onClick={actions.handleSaveCapitalDecrease}
            disabled={
              !buttonEnabledScriptureView && !buttonEnabledMovementsView
            }
          >
            {buttonText}
          </button>
        </div>
      </Modal.Footer>
    </>
  );
};

export default CapitalDecreaseModalFooter;
